import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Share2, ExternalLink } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function getEmbedUrl(postUrl) {
  return `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(postUrl)}&show_text=true&width=500`;
}

function getShareUrl(postUrl) {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`;
}

export default function FacebookSection() {
  const [postUrl, setPostUrl] = useState("");
  const [shareCount, setShareCount] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/api/facebook-post`)
      .then((res) => setPostUrl(res.data.url))
      .catch(() => {});
    axios
      .get(`${API}/api/share-count`)
      .then((res) => setShareCount(res.data.count))
      .catch(() => {});
  }, []);

  const handleShare = () => {
    axios.post(`${API}/api/share-track`).catch(() => {});
    setShareCount((c) => c + 1);
    window.open(getShareUrl(postUrl), "_blank", "width=600,height=400");
  };

  if (!postUrl) return null;

  return (
    <section className="py-20 bg-slate-900" data-testid="facebook-section" id="facebook-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left side */}
          <div className="space-y-6">
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/60 border border-slate-700"
              data-testid="facebook-badge"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-sm font-medium text-slate-300 tracking-wide">
                New on Facebook
              </span>
            </div>

            <div>
              <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
                On Facebook
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-100 mt-3">
                Latest From the Campaign
              </h2>
            </div>

            <p className="text-lg text-slate-400 leading-relaxed">
              Stay connected with the campaign. Share the latest update with your friends and family on Facebook.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleShare}
                className="bg-orange-600 hover:bg-orange-500 text-white px-6 py-5 rounded-full font-semibold transition-all hover:-translate-y-0.5"
                data-testid="facebook-share-btn"
              >
                <Share2 className="mr-2 w-4 h-4" />
                Share on your Facebook
              </Button>
              <a
                href={postUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="facebook-read-btn"
              >
                <Button
                  variant="outline"
                  className="bg-transparent border-2 border-slate-600 hover:border-slate-400 text-slate-200 hover:text-white px-6 py-5 rounded-full font-semibold transition-all"
                >
                  <ExternalLink className="mr-2 w-4 h-4" />
                  Read on Facebook
                </Button>
              </a>
            </div>

            {shareCount > 0 && (
              <p className="text-sm text-slate-400" data-testid="share-count">
                Shared <span className="text-emerald-400 font-semibold">{shareCount}</span> time{shareCount !== 1 ? "s" : ""} from this site
              </p>
            )}
          </div>

          {/* Right side - Facebook embed */}
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-emerald-900/20 to-orange-900/20 rounded-3xl blur-xl" />
            <div className="relative bg-slate-800/50 border border-slate-700 rounded-2xl p-4 overflow-hidden">
              <iframe
                src={getEmbedUrl(postUrl)}
                width="500"
                height="600"
                className="w-full max-w-[500px] mx-auto border-0 rounded-lg bg-white"
                style={{ maxWidth: "100%" }}
                scrolling="no"
                frameBorder="0"
                allowFullScreen={true}
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                title="Facebook post"
                data-testid="facebook-embed"
              />
              <p className="text-center mt-4">
                <a
                  href={postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
                  data-testid="facebook-fallback-link"
                >
                  Can't see the post? View on Facebook
                  <ExternalLink className="inline ml-1 w-3 h-3" />
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
